/**
 * Cuánto tiempo está ocupado un muelle.
 *
 * La trampa está en que **es una unión de intervalos, no una suma**. Dos camiones
 * programados encima en el mismo muelle de 08:00 a 10:00 no ocupan cuatro horas:
 * ocupan dos, y además mal. Sumar las duraciones daría una ocupación por encima
 * del 100 % sin que nadie entienda por qué, y en un muelle muy solapado podría
 * dar el 300 %.
 */
import { MS_HORA } from '@/utils/planningDateRanges'

export interface Intervalo {
  iniMs: number
  finMs: number
}

/** Horas ocupadas, contando una sola vez el tiempo solapado y recortando al
 *  rango visible. */
export function horasOcupadas(
  intervalos: Intervalo[],
  rangoIniMs: number,
  rangoFinMs: number,
): number {
  const recortados = intervalos
    .map(({ iniMs, finMs }) => ({
      iniMs: Math.max(iniMs, rangoIniMs),
      finMs: Math.min(finMs, rangoFinMs),
    }))
    .filter((i) => i.finMs > i.iniMs)
    .sort((a, b) => a.iniMs - b.iniMs)

  let total = 0
  let curIni = 0
  let curFin = 0
  let abierto = false

  for (const { iniMs, finMs } of recortados) {
    if (!abierto) {
      curIni = iniMs
      curFin = finMs
      abierto = true
      continue
    }
    if (iniMs <= curFin) {
      // Se tocan o se solapan: se funden en uno.
      curFin = Math.max(curFin, finMs)
    } else {
      total += curFin - curIni
      curIni = iniMs
      curFin = finMs
    }
  }
  if (abierto) total += curFin - curIni

  return total / MS_HORA
}

export interface Ocupacion {
  /** Horas realmente ocupadas. */
  horas: number
  /** Horas que el muelle está abierto en el rango. */
  disponibles: number
  /** 0–100. `0` cuando no hay nada, nunca `NaN`. */
  pct: number
}

export function ocupacionDe(
  intervalos: Intervalo[],
  rangoIniMs: number,
  rangoFinMs: number,
  horasDisponibles: number,
): Ocupacion {
  const horas = horasOcupadas(intervalos, rangoIniMs, rangoFinMs)
  // Un muelle sin jornada declarada da 0 %, no una división por cero: en la
  // pantalla un `NaN%` es peor que un cero, porque no se sabe si falta el dato o
  // falta el muelle.
  const pct = horasDisponibles > 0 ? (horas / horasDisponibles) * 100 : 0
  return { horas, disponibles: horasDisponibles, pct }
}
